import React, { useState, useEffect } from "react";
import { usePlatformAuth } from "context/PlatformAuthContext";
import { platformApiService } from "services/platformApi";
import { useTranslation } from "react-i18next";
import { FaEnvelope, FaEnvelopeOpen, FaTrash, FaSpinner } from "react-icons/fa";

interface ContactMessage {
 _id: string;
 name: string;
 email: string;
 subject: string;
 message: string;
 isRead: boolean;
 readAt?: string;
 createdAt: string;
}

const ContactMessages: React.FC = () => {
 const { isAuthenticated } = usePlatformAuth();
 const { t } = useTranslation();
 const [messages, setMessages] = useState<ContactMessage[]>([]);
 const [loading, setLoading] = useState(true);
 const [selectedMessage, setSelectedMessage] = useState<ContactMessage | null>(null);
 const [filter, setFilter] = useState<"all" | "unread" | "read">("all");
 const [page, setPage] = useState(1);
 const [totalPages, setTotalPages] = useState(1);
 const [unreadCount, setUnreadCount] = useState(0);

 useEffect(() => {
  if (isAuthenticated) {
   fetchMessages();
  }
 }, [isAuthenticated, filter, page]);

 const fetchMessages = async () => {
  try {
   setLoading(true);
   const params: any = { page, limit: 20 };
   if (filter === "unread") params.isRead = false;
   if (filter === "read") params.isRead = true;

   const response = await platformApiService.getContactMessages(params);
   setMessages(response.data.messages || []);
   setTotalPages(response.data.pagination?.pages || 1);
   setUnreadCount(response.data.unreadCount || 0);
  } catch (error: any) {
   console.error("Error fetching contact messages:", error);
  } finally {
   setLoading(false);
  }
 };

 const handleMarkAsRead = async (messageId: string) => {
  try {
   await platformApiService.markContactMessageAsRead(messageId);
   setMessages((prev) =>
    prev.map((msg) =>
     msg._id === messageId ? { ...msg, isRead: true, readAt: new Date().toISOString() } : msg
    )
   );
   if (selectedMessage?._id === messageId) {
    setSelectedMessage((prev) => (prev ? { ...prev, isRead: true, readAt: new Date().toISOString() } : null));
   }
   setUnreadCount((prev) => Math.max(0, prev - 1));
  } catch (error: any) {
   console.error("Error marking message as read:", error);
  }
 };

 const handleDelete = async (messageId: string) => {
  if (!window.confirm(t("platform.contactMessages.confirmDelete") || "Are you sure you want to delete this message?")) {
   return;
  }
  try {
   await platformApiService.deleteContactMessage(messageId);
   setMessages((prev) => prev.filter((msg) => msg._id !== messageId));
   if (selectedMessage?._id === messageId) {
    setSelectedMessage(null);
   }
  } catch (error: any) {
   console.error("Error deleting message:", error);
  }
 };

 const handleViewMessage = async (message: ContactMessage) => {
  setSelectedMessage(message);
  if (!message.isRead) {
   await handleMarkAsRead(message._id);
  }
 };

 if (!isAuthenticated) {
  return (
   <div className="p-8">
    <p>{t("platform.contactMessages.unauthorized") || "Please log in to view contact messages."}</p>
   </div>
  );
 }

 return (
  <div className="p-8">
   <div className="mb-6 flex justify-between items-center">
    <div>
     <h1 className="text-3xl font-bold text-gray-900">
      {t("platform.contactMessages.title") || "Contact Messages"}
     </h1>
     <p className="text-gray-600 mt-2">
      {unreadCount > 0 && (
       <span className="bg-red-100 text-red-800 px-3 py-1 text-sm font-semibold">
        {unreadCount} {t("platform.contactMessages.unread") || "unread"}
       </span>
      )}
     </p>
    </div>
   </div>

   {/* Filters */}
   <div className="mb-6 flex gap-2">
    <button
     onClick={() => {
      setFilter("all");
      setPage(1);
     }}
     className={`px-4 py-2 font-medium transition-colors ${
      filter === "all" ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
     }`}
    >
     {t("platform.contactMessages.all") || "All"}
    </button>
    <button
     onClick={() => {
      setFilter("unread");
      setPage(1);
     }}
     className={`px-4 py-2 font-medium transition-colors ${
      filter === "unread" ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
     }`}
    >
     {t("platform.contactMessages.unread") || "Unread"}
    </button>
    <button
     onClick={() => {
      setFilter("read");
      setPage(1);
     }}
     className={`px-4 py-2 font-medium transition-colors ${
      filter === "read" ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
     }`}
    >
     {t("platform.contactMessages.read") || "Read"}
    </button>
   </div>

   <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
    {/* Messages List */}
    <div className="lg:col-span-1">
     <div className="bg-white shadow">
      {loading ? (
       <div className="p-8 text-center">
        <FaSpinner className="animate-spin text-indigo-600 text-2xl mx-auto mb-4" />
        <p className="text-gray-600">{t("platform.contactMessages.loading") || "Loading messages..."}</p>
       </div>
      ) : messages.length === 0 ? (
       <div className="p-8 text-center text-gray-500">
        <FaEnvelope className="text-4xl mx-auto mb-4 opacity-50" />
        <p>{t("platform.contactMessages.noMessages") || "No messages found."}</p>
       </div>
      ) : (
       <div className="divide-y">
        {messages.map((message) => (
         <div
          key={message._id}
          onClick={() => handleViewMessage(message)}
          className={`p-4 cursor-pointer transition-colors ${
           selectedMessage?._id === message._id
            ? "bg-blue-50 border-l-4 border-indigo-600"
            : message.isRead
            ? "hover:bg-gray-50"
            : "bg-blue-50 hover:bg-blue-100"
          }`}
         >
          <div className="flex items-start justify-between mb-2">
           <div className="flex-1">
            <div className="flex items-center gap-2">
             {message.isRead ? (
              <FaEnvelopeOpen className="text-gray-400 text-sm" />
             ) : (
              <FaEnvelope className="text-blue-600 text-sm" />
             )}
             <h3 className="font-semibold text-gray-900 truncate">{message.name}</h3>
            </div>
            <p className="text-sm text-gray-600 truncate">{message.email}</p>
           </div>
           <button
            onClick={(e) => {
             e.stopPropagation();
             handleDelete(message._id);
            }}
            className="text-red-500 hover:text-red-700 p-1"
            title={t("platform.contactMessages.delete") || "Delete"}
           >
            <FaTrash className="text-sm" />
           </button>
          </div>
          <p className="text-sm font-medium text-gray-900 mb-1 truncate">{message.subject}</p>
          <p className="text-xs text-gray-500">
           {new Date(message.createdAt).toLocaleDateString()} {new Date(message.createdAt).toLocaleTimeString()}
          </p>
         </div>
        ))}
       </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
       <div className="p-4 border-t flex justify-between items-center">
        <button
         onClick={() => setPage((p) => Math.max(1, p - 1))}
         disabled={page === 1}
         className="px-4 py-2 bg-gray-100 text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-200"
        >
         {t("platform.contactMessages.previous") || "Previous"}
        </button>
        <span className="text-gray-600">
         {t("platform.contactMessages.page") || "Page"} {page} / {totalPages}
        </span>
        <button
         onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
         disabled={page === totalPages}
         className="px-4 py-2 bg-gray-100 text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-200"
        >
         {t("platform.contactMessages.next") || "Next"}
        </button>
       </div>
      )}
     </div>
    </div>

    {/* Message Detail */}
    <div className="lg:col-span-2">
     {selectedMessage ? (
      <div className="bg-white shadow p-6">
       <div className="flex justify-between items-start mb-4">
        <div>
         <h2 className="text-2xl font-bold text-gray-900 mb-2">{selectedMessage.subject}</h2>
         <div className="flex items-center gap-4 text-sm text-gray-600">
          <span>
           <strong>{t("platform.contactMessages.from") || "From"}:</strong> {selectedMessage.name}
          </span>
          <span>
           <strong>{t("platform.contactMessages.email") || "Email"}:</strong>{" "}
           <a href={`mailto:${selectedMessage.email}`} className="text-indigo-600 hover:underline">
            {selectedMessage.email}
           </a>
          </span>
         </div>
         <p className="text-sm text-gray-500 mt-2">
          {new Date(selectedMessage.createdAt).toLocaleString()}
         </p>
        </div>
        <div className="flex gap-2">
         {!selectedMessage.isRead && (
          <button
           onClick={() => handleMarkAsRead(selectedMessage._id)}
           className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 text-sm"
          >
           <FaEnvelopeOpen className="inline mr-2" />
           {t("platform.contactMessages.markAsRead") || "Mark as Read"}
          </button>
         )}
         <button
          onClick={() => handleDelete(selectedMessage._id)}
          className="px-4 py-2 bg-red-600 text-white hover:bg-red-700 text-sm"
         >
          <FaTrash className="inline mr-2" />
          {t("platform.contactMessages.delete") || "Delete"}
         </button>
        </div>
       </div>
       <div className="border-t pt-4">
        <h3 className="font-semibold text-gray-900 mb-2">{t("platform.contactMessages.message") || "Message"}:</h3>
        <div className="prose max-w-none">
         <p className="text-gray-700 whitespace-pre-wrap">{selectedMessage.message}</p>
        </div>
       </div>
      </div>
     ) : (
      <div className="bg-white shadow p-12 text-center text-gray-500">
       <FaEnvelope className="text-6xl mx-auto mb-4 opacity-50" />
       <p>{t("platform.contactMessages.selectMessage") || "Select a message to view details"}</p>
      </div>
     )}
    </div>
   </div>
  </div>
 );
};

export default ContactMessages;

